import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Gavel as GavelIcon,
  Send as SendIcon,
  CheckCircle as SignedIcon,
  Warning as ExpiringIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { format, parseISO, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import type { Contract } from '../types';
import {
  contractService,
  contractStatusColors,
  contractStatusLabels,
  contractTypeLabels,
} from '../services/contractService';
import { colors } from '../theme';

const ContractList: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [filteredContracts, setFilteredContracts] = useState<Contract[]>([]);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);

  // =========================================================================
  // Carga inicial de datos
  // =========================================================================

  useEffect(() => {
    contractService.initializeIfEmpty();
    loadContracts();

    const handleContractUpdate = () => loadContracts();
    window.addEventListener('contractsUpdated', handleContractUpdate);
    return () => window.removeEventListener('contractsUpdated', handleContractUpdate);
  }, []);

  const loadContracts = () => {
    const all = contractService.getAll();
    setContracts(all);
  };

  // =========================================================================
  // Filtrado
  // =========================================================================

  useEffect(() => {
    let result = [...contracts];

    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      result = result.filter(c => c.type === typeFilter);
    }

    if (searchText.trim()) {
      const search = searchText.toLowerCase().trim();
      result = result.filter(c =>
        c.contractNumber.toLowerCase().includes(search) ||
        c.title.toLowerCase().includes(search) ||
        c.supplierName.toLowerCase().includes(search)
      );
    }

    result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    setFilteredContracts(result);
    setPage(0);
  }, [contracts, statusFilter, typeFilter, searchText]);

  // =========================================================================
  // KPIs
  // =========================================================================

  const kpiTotal = contracts.length;
  const kpiActive = contracts.filter(c => c.status === 'signed').length;
  const kpiPendingSign = contracts.filter(c => c.status === 'sent_to_sign').length;
  const kpiExpiringSoon = contracts.filter(c => {
    if (c.status !== 'signed') return false;
    const daysToExpiry = differenceInDays(parseISO(c.endDate), new Date());
    return daysToExpiry >= 0 && daysToExpiry <= 30;
  }).length;

  // =========================================================================
  // Handlers
  // =========================================================================

  const handleDeleteClick = (contract: Contract) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (contractToDelete) {
      contractService.delete(contractToDelete.id);
      loadContracts();
    }
    setDeleteDialogOpen(false);
    setContractToDelete(null);
  };

  // =========================================================================
  // Helpers
  // =========================================================================

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: es });
    } catch {
      return '—';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
  };

  const getDaysToExpiry = (endDate: string): number => {
    try {
      return differenceInDays(parseISO(endDate), new Date());
    } catch {
      return 0;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Contratos LTP
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestión de contratos a largo plazo con proveedores
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/contracts/new')}
          sx={{
            bgcolor: colors.brand.red,
            '&:hover': { bgcolor: colors.primary.dark },
          }}
        >
          Nuevo Contrato
        </Button>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <GavelIcon sx={{ fontSize: 32, color: colors.brand.blue, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiTotal}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Contratos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <SignedIcon sx={{ fontSize: 32, color: contractStatusColors.signed, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiActive}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Activos
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <SendIcon sx={{ fontSize: 32, color: contractStatusColors.sent_to_sign, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiPendingSign}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pendientes Firma
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <ExpiringIcon sx={{ fontSize: 32, color: colors.warning.main, mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {kpiExpiringSoon}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Por Vencer (30d)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por número, título o proveedor..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="draft">Borrador</MenuItem>
              <MenuItem value="review">En Revisión</MenuItem>
              <MenuItem value="sent_to_sign">Pendiente Firma</MenuItem>
              <MenuItem value="signed">Firmado</MenuItem>
              <MenuItem value="expired">Vencido</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              select
              label="Tipo"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="service">Servicio</MenuItem>
              <MenuItem value="supply">Suministro</MenuItem>
              <MenuItem value="framework">Contrato Marco</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {filteredContracts.length} resultado{filteredContracts.length !== 1 ? 's' : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell align="center">Tipo</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Vigencia</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contract) => {
                  const daysToExpiry = getDaysToExpiry(contract.endDate);
                  const isExpiringSoon = contract.status === 'signed' && daysToExpiry >= 0 && daysToExpiry <= 30;

                  return (
                    <TableRow
                      key={contract.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/contracts/${contract.id}`)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: colors.brand.blue }}
                        >
                          {contract.contractNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {contract.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contract.supplierName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={contractTypeLabels[contract.type]}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {formatCurrency(contract.value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(contract.startDate)} — {formatDate(contract.endDate)}
                        </Typography>
                        {isExpiringSoon && (
                          <Typography variant="caption" sx={{ color: colors.warning.dark, fontWeight: 600 }}>
                            Vence en {daysToExpiry} día{daysToExpiry !== 1 ? 's' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={contractStatusLabels[contract.status]}
                          size="small"
                          sx={{
                            bgcolor: contractStatusColors[contract.status],
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                        {contract.status === 'sent_to_sign' && contract.docusignEnvelopeId && (
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 0.5, color: colors.info.main, fontWeight: 500 }}
                          >
                            DocuSign
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          <Tooltip title="Ver detalle">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contracts/${contract.id}`);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {contract.status === 'draft' && (
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(contract);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filteredContracts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <GavelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No se encontraron contratos
                    </Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
                      {contracts.length === 0
                        ? 'Cree su primer contrato usando el botón "Nuevo Contrato"'
                        : 'Intente modificar los filtros de búsqueda'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredContracts.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Contrato</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el contrato <strong>{contractToDelete?.contractNumber}</strong> — "{contractToDelete?.title}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContractList;
